import { useNavigate, useParams } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { SubmitEvent, useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { ADD_BATCH_MUTATION, CALCULATE_BATCH_MUTATION } from '../../mutations/BatchMutations';
import { GET_BATCHES_QUERY } from '../../queries/BatchQueries';
import { Alert, Box, Button, CircularProgress, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { Product } from '../../models/Product';
import { GET_PRODUCT_BY_ID_QUERY } from '../../queries/ProductQueries';
import { Batch } from '../../models/Batch';
import {
  DataGrid,
  GridColDef,
  GridRowClassNameParams,
  gridClasses,
} from '@mui/x-data-grid';
import { ConsumedInventoryItem } from '../../models/ConsumedInventoryItem';
import dayjs from "dayjs";
import NumberField from '../NumberField';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import { unitTypeToUnits } from '../../models/Item';

const INITIAL_PAGE_SIZE = 100;

export default function BatchCreate() {
  const { productId } = useParams();

  const navigate = useNavigate();

  const notifications = useNotifications();

  const [getProductById] = useLazyQuery(
    GET_PRODUCT_BY_ID_QUERY);

  const [calculateBatchForProduct] = useMutation(
    CALCULATE_BATCH_MUTATION);

  const [createBatch] = useMutation(
    ADD_BATCH_MUTATION, {
    refetchQueries: [{ query: GET_BATCHES_QUERY }],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [isCalculatingBatch, setIsCalculatingBatch] = useState(false);
  const [calculatedBatch, setCalculatedBatch] = useState<Batch | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(productId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getProductByIdError } = await getProductById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getProductByIdError) {
          setError(getProductByIdError);
        }

        if (data?.product) {
          setProduct(data.product);
        } else {
          setError({ message: 'No product found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getProductById, productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onAmountChange = useCallback(
    async (value: number | null, fieldName: string) => {
      setAmount(value || 0);
    },
    [],
  );

  const calculateBatch = useCallback(async () => {
    if ((amount || 0) > 0) {
      setIsCalculatingBatch(true);

      const { data: result } = await calculateBatchForProduct({
        variables: {
          productId: +(productId || '0'),
          amount: amount
        }
      });

      setIsCalculatingBatch(false);

      if (result?.batch) {
        setCalculatedBatch(result.batch);
      } else {
        notifications.show(
          'Failed to calculate product batch. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    }
  }, [amount, calculateBatchForProduct, notifications, productId]);

  const submitBatch = useCallback(async (event: SubmitEvent) => {
    event.preventDefault();

    setIsSubmitting(true);

    try {
      const { data: result } = await createBatch({
        variables: {
          productId: +(productId || '0'),
          amount: amount
        }
      });

      if (result?.added) {
        navigate('/dashboard/batches');
      } else {
        notifications.show(
          'Failed to create batch. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create batch. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );

      throw createError;
    }
  }, [createBatch, productId, amount, navigate, notifications]);

  const handleBack = useCallback(() => {
    navigate(`/dashboard/products/${productId}`);
  }, [navigate, productId]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'itemName',
        headerName: 'Item',
        flex: .50,
        display: 'flex',
        valueGetter: (_, row) => row.item?.name
      },
      {
        field: 'productBatchQuantity',
        headerName: 'Qty Needed (lbs)',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueFormatter: (value: number | null) => (value || 0).toFixed(3)
      },
      {
        field: 'remainingProductBatchQuantity',
        headerName: 'Rem Qty Needed (lbs)',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueFormatter: (value: number | null) => (value || 0).toFixed(3)
      },
      {
        field: 'calculatedItemCost',
        headerName: 'Total Item Cost ($)',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueFormatter: (value: number | null) => toUSCurrency(value || 0)
      },
      {
        field: 'calculatedInventoryItems',
        headerName: 'Calculated Inventory Items',
        sortable: false,
        filterable: false,
        flex: 5,
        minWidth: 600,
        renderCell: (params) => {
          return (
            (params.row.calculatedConsumedInventoryItems || []).length === 0 ?
              <Box sx={{ color: '#fac1c0' }}>
                No items available.
              </Box> : (
                <Table sx={{ minWidth: 600 }} aria-label="dense table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} component="th" scope="row" align="left">Vendor</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="left">Lot #</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="left">Date Ordered</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">QOH</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">Qty Used</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">ROH</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} component="th" scope="row" align="right">Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(params.row.calculatedConsumedInventoryItems || []).map((consumedItem: ConsumedInventoryItem) => (
                      <TableRow
                        key={consumedItem.inventoryItem?.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} align="left">
                          {consumedItem.inventoryItem?.vendor?.name}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="left">
                          {consumedItem.inventoryItem?.lotNumber || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="left">
                          {dayjs(consumedItem.inventoryItem?.purchaseOrder?.dateTimePurchased).format('M/D/YYYY') || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          {(consumedItem.inventoryItem?.quantityOnHand ?? 0).toFixed(3)} {unitTypeToUnits(consumedItem.inventoryItem?.item?.unitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          {consumedItem.quantityUsed?.toFixed(3)} {unitTypeToUnits(consumedItem.inventoryItem?.item?.unitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          {consumedItem.inventoryItem?.remainingQuantityOnHand?.toFixed(3)} {unitTypeToUnits(consumedItem.inventoryItem?.item?.unitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '14%' }} align="right">
                          ${toUSCurrency(consumedItem.totalItemCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
          );
        }
      }
    ],
    [],
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    if (!params.row.productBatchQuantityMet) {
      return 'product-batch-quantity-not-met';
    }

    return '';
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          m: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  return (
    <PageContainer
      title={`New Batch for "${product?.name}"`}
      breadcrumbs={[{ title: 'Batch', path: '/dashboard/batches' }, { title: 'New' }]}
    >
      <Box
        component="form"
        onSubmit={submitBatch}
        noValidate
        autoComplete="off"
        sx={{ width: '100%' }}
      >
        <Grid container spacing={1} sx={{ mb: 2, width: '100%' }}>
          <Grid container spacing={0} sx={{ mb: 2, width: '25%' }} style={{ marginBottom: 0 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
              <NumberField
                value={amount || null}
                onValueChange={(event) => onAmountChange(event, 'amount')}
                name="amount"
                label="Amount"
                error={amount === undefined || amount <= .1 || amount > 1000}
                helperText={amount === undefined || amount <= .1 || amount > 1000 ? 'Please enter a valid amount.' : ' '}
                weightFieldUnits="lbs"
                fullWidth
              />
              <Box sx={{ paddingTop: .75 }}>
                <Button
                  type="button"
                  variant="contained"
                  size="medium"
                  disabled={amount === undefined || amount <= .1 || amount > 1000}
                  loading={isCalculatingBatch}
                  onClick={calculateBatch}
                >
                  Calculate
                </Button>
              </Box>
            </Stack>
          </Grid>
          {calculatedBatch ?
            <Box sx={{ height: '56vh', width: '100%' }}>
              <DataGrid
                density="compact"
                rows={calculatedBatch.calculatedProductItems}
                columns={columns}
                loading={isLoading}
                pageSizeOptions={[INITIAL_PAGE_SIZE]}
                getRowHeight={() => 'auto'}
                getRowClassName={getRowClassName}
                sx={[
                  () => ({
                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                    '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                    [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                      outline: 'transparent',
                    },
                    [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                    {
                      outline: 'none',
                    },
                    [`& .${gridClasses.row}:hover`]: {
                      cursor: 'pointer',
                    },
                    '.product-batch-quantity-not-met': {
                      backgroundColor: '#f8d7c0',
                      '&:hover': {
                        backgroundColor: 'rgba(248,215,192,.75)',
                        filter: 'brightness(105%)'
                      }
                    }
                  }),
                  (theme) =>
                    theme.applyStyles('dark', {
                      '.product-batch-quantity-not-met': {
                        backgroundColor: '#ed201c',
                        '&:hover': {
                          backgroundColor: '#ed201c',
                          filter: 'brightness(120%)'
                        }
                      }
                    })]}
                slotProps={{
                  loadingOverlay: {
                    variant: 'circular-progress',
                    noRowsVariant: 'circular-progress',
                  },
                  baseIconButton: {
                    size: 'small',
                  },
                }}
              />
            </Box>
            : <></>
          }
        </Grid>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Box sx={{ pt: 1.5 }}>
              Total (lbs): {(calculatedBatch?.calculatedProductItems?.map(item => item.productBatchQuantity || 0)?.reduce((a, b) => a + b, 0) || 0).toFixed(3)}
            </Box>
            <Box sx={{ paddingTop: .75 }}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={amount === undefined || amount <= .1 || amount > 1000 || (calculatedBatch?.calculatedProductItems || []).length === 0}
                loading={isCalculatingBatch || isSubmitting}
              >
                Create Batch
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </PageContainer>
  );
}
